/**
 * ProgressStream — real-time scrape progress panel with phase-by-phase status rows,
 * determinate progress bar, lucide icons, and a collapsible raw-log section.
 *
 * @description
 *   Renders the structured output of useScrapeStream: a progress bar that advances
 *   through named phases, an icon-per-status row for each phase (with optional
 *   indented substeps for the pipeline phase), a result row with price + technique,
 *   and a collapsible technical-details log. Intentionally zero emojis.
 *
 * @useWhen
 *   - Displaying real-time scrape progress in ScraperConfigForm (test & persist runs)
 *   - Showing per-row scrape progress in ScraperList
 *   - Any surface that calls useScrapeStream and needs to render its state
 *
 * @dontUseFor
 *   - Indeterminate background jobs — use LinearProgress without determinate value
 *   - Progress indicators that are not scrape-stream shaped — use ProgressButton instead
 *   - Showing static historical scrape results — render fields directly, no stream needed
 *
 * @related
 *   - ProgressButton (common) — button-level progress for short determinate jobs
 *   - CollapsibleSection (common) — used internally for the raw-log panel
 *   - useScrapeStream (hooks) — the hook that produces the state this component consumes
 *
 * @reuseHints
 *   - Reusable on any SSE-driven pipeline that maps to {status, phases, progress, result, rawLog}
 *
 * @props
 *   title      string?   Optional panel heading. Omit when the container
 *                        (DialogTitle / section heading) already shows one —
 *                        passing it then renders a duplicate. Pass it for
 *                        inline widgets that have no surrounding heading.
 *   status     string    'idle'|'running'|'success'|'error'
 *   progress   object    {current, total, label} — drives the determinate bar
 *   phases     array     [{key,label,status,detail,substeps}]
 *   result     object?   {price,currency,availability,technique} — shown when present
 *   rawLog     string[]  Raw log lines; shown collapsed in Technical details
 */

import React from 'react';
import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Circle, Loader2, Check, Minus, AlertTriangle } from 'lucide-react';
import CollapsibleSection from '../CollapsibleSection';
import { SP, FS } from '../../../theme/tokens';

// ── Icon map ──────────────────────────────────────────────────────
function PhaseIcon({ status }) {
  const size = 15;
  switch (status) {
    case 'running':
      return (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            color: 'primary.main',
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'spin 1s linear infinite',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
            },
          }}
        >
          <Loader2 size={size} />
        </Box>
      );
    case 'done':
    case 'success':
      return (
        <Box component="span" sx={{ display: 'inline-flex', color: 'success.main' }}>
          <Check size={size} />
        </Box>
      );
    case 'skipped':
      return (
        <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled' }}>
          <Minus size={size} />
        </Box>
      );
    case 'failed':
    case 'error':
      return (
        <Box component="span" sx={{ display: 'inline-flex', color: 'error.main' }}>
          <AlertTriangle size={size} />
        </Box>
      );
    default:
      // pending
      return (
        <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled' }}>
          <Circle size={size} />
        </Box>
      );
  }
}

// ── Progress bar color ────────────────────────────────────────────
function barColor(status) {
  if (status === 'error') return 'error';
  if (status === 'success') return 'success';
  return 'primary';
}

// ── Price formatter — app-canonical de-DE currency (mirrors MarketPricingOverview
//    `money()`), so a scraped price reads "279,00 €" not a bare "279". Defaults to
//    EUR when the scrape result carries no currency (POG is EUR-centric). ──
function money(value, currency) {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR' }).format(n);
  } catch (_e) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
  }
}

// ── Component ─────────────────────────────────────────────────────
export default function ProgressStream({
  title,
  status = 'idle',
  progress = { current: 0, total: 5, label: '' },
  phases = [],
  result = null,
  rawLog = [],
}) {
  const { current, total, label } = progress;
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    // No Paper/PANEL_SX wrapper here — every consumer already renders this inside a
    // container (DialogContent, the Test Output Paper, the dashboard widget), so a
    // panel here produced a "container-in-container" nested-card look. Stay bare.
    <Box>
      <Stack spacing={SP.tight}>
        {/* Header — title is optional (the container, e.g. a DialogTitle, usually
            provides it; passing title here inside a titled modal double-headlines).
            The progress label still renders on its own. */}
        {(title || label) && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={title ? 'space-between' : 'flex-end'}
            spacing={SP.tight}
          >
            {title ? (
              <Typography variant="h6" sx={{ fontSize: FS.lg, fontWeight: 600 }}>
                {title}
              </Typography>
            ) : null}
            {label ? (
              <Typography variant="caption" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                {label}
              </Typography>
            ) : null}
          </Stack>
        )}

        {/* Determinate progress bar */}
        <LinearProgress
          variant="determinate"
          value={pct}
          color={barColor(status)}
          sx={{ borderRadius: 4, height: 6 }}
        />

        {/* Phase rows */}
        {phases.length > 0 && (
          <Stack spacing={0.5} sx={{ pt: SP.chip }}>
            {phases.map((phase) => (
              <React.Fragment key={phase.key}>
                {/* Phase row */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PhaseIcon status={phase.status} />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontSize: FS.base,
                      color: phase.status === 'skipped' ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {phase.label}
                  </Typography>
                  {phase.detail ? (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: FS.sm, color: 'text.secondary', textAlign: 'right' }}
                    >
                      {phase.detail}
                    </Typography>
                  ) : null}
                </Stack>

                {/* Substeps (pipeline phase) */}
                {phase.substeps && phase.substeps.length > 0 && (
                  <Stack spacing={0.25} sx={{ pl: SP.card }}>
                    {phase.substeps.map((step, idx) => (
                      <Stack
                        key={idx}
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                      >
                        <PhaseIcon status={step.status} />
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            fontSize: FS.sm,
                            color: step.status === 'skipped' ? 'text.disabled' : 'text.secondary',
                          }}
                        >
                          {step.name}
                        </Typography>
                        {step.detail ? (
                          <Typography variant="caption" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                            {step.detail}
                          </Typography>
                        ) : null}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </React.Fragment>
            ))}
          </Stack>
        )}

        {/* Result row */}
        {result && (
          <Box
            sx={{
              pt: SP.tight,
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: SP.chip,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography
                sx={{ fontWeight: 700, fontSize: FS.xl, color: 'text.primary' }}
              >
                {money(result.price, result.currency)}
              </Typography>
              {result.availability != null && (
                <Chip
                  // Match the scraper surfaces' wording (ModernLastScrapeSummary,
                  // ScraperList) — "In Stock" / "Out of Stock", not Available/Unavailable.
                  label={result.availability ? 'In Stock' : 'Out of Stock'}
                  size="small"
                  color={result.availability ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ fontSize: FS.xs }}
                />
              )}
              {result.technique && (
                <Typography
                  variant="caption"
                  sx={{ fontSize: FS.sm, color: 'text.disabled', ml: 'auto' }}
                >
                  via {result.technique}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Raw log */}
        {rawLog && rawLog.length > 0 && (
          <CollapsibleSection title="Technical details" defaultExpanded={false}>
            <Stack spacing={0.25}>
              {rawLog.map((line, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  component="div"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: FS.xs,
                    color: 'text.disabled',
                    wordBreak: 'break-all',
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Stack>
          </CollapsibleSection>
        )}
      </Stack>
    </Box>
  );
}
